import type { PostPurpose, PostStatus } from "@unik/shared";
import { statusLabel } from "@unik/shared";

/**
 * Selos de estado e de função.
 *
 * Regra do doc 06 §5.4: a semântica NUNCA depende só de cor — todo selo carrega
 * texto. Isso atende daltonismo e resolve a proximidade visual entre o vermelho
 * de erro (#E8455F) e o carmim de acento (#B3352B).
 */

const NEUTRAL = "bg-[var(--border-subtle)] text-text-secondary";

const STATUS_STYLE: Record<PostStatus, string> = {
  draft: NEUTRAL,
  pending_approval: NEUTRAL,
  revision_requested: "bg-[var(--warning-soft)] text-warning",
  approved: "bg-[var(--success-soft)] text-success",
  needs_reschedule: "bg-[var(--warning-soft)] text-warning",
  publishing: NEUTRAL,
  published: "bg-[var(--success-soft)] text-success",
  failed: "bg-[var(--danger-soft)] text-danger",
  archived: "bg-[var(--border-subtle)] text-text-tertiary",
};

export function StatusBadge({
  status,
  audience,
}: {
  status: PostStatus;
  audience: "admin" | "client";
}) {
  const label = statusLabel(status, audience);
  if (label === null) return null;

  // Para a cliente, estados técnicos (publishing/failed/needs_reschedule) já vêm
  // rotulados como "Aprovado" — então o estilo precisa acompanhar o rótulo, não o estado real.
  const style =
    audience === "client" && label === "Aprovado"
      ? STATUS_STYLE.approved
      : STATUS_STYLE[status];

  return (
    <span
      className={`type-overline inline-flex h-[22px] items-center rounded-[var(--radius-control-sm)] px-2 ${style}`}
    >
      {label}
    </span>
  );
}

/** Método LL, movimento III — o selo que traduz a marca para a tela. */
const PURPOSE_STYLE: Record<PostPurpose, string> = {
  aquecer: "text-text-secondary border border-border-subtle",
  qualificar: "text-gold border border-[color-mix(in_srgb,var(--gold)_35%,transparent)]",
  vender: "text-text-on-brand bg-surface-brand",
};

export function PurposeBadge({ purpose }: { purpose: PostPurpose }) {
  return (
    <span
      className={`type-overline inline-flex h-[22px] items-center rounded-[var(--radius-control-sm)] px-2 ${PURPOSE_STYLE[purpose]}`}
    >
      {purpose}
    </span>
  );
}
